import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * A fala do Lucas, com a marcação interpretada.
 *
 * O balão mostrava texto puro enquanto o modelo escreve markdown, então
 * `**Início**` chegava ao usuário com os asteriscos à mostra. O reescritor de
 * nomes agravou isso: ao trocar `input` por "Aguardar Resposta" ele envolve o
 * nome em `**`, que é a convenção certa — faltava alguém interpretá-la.
 *
 * Não reaproveita o `MarkdownRenderer` dos artigos de propósito: aquele traz as
 * classes `prose` inteiras, dimensionadas para uma página de documentação, e
 * dentro do balão elas brigam com a tipografia da conversa.
 *
 * O subconjunto é pequeno porque um turno de suporte é uma resposta curta:
 * negrito, itálico, código, lista e link. Título, tabela e imagem não entram —
 * para mostrar coisa existe bloco, que é renderizado à parte.
 *
 * Sem `rehype-raw`: HTML no texto fica escapado. O conteúdo vem de um modelo e
 * pode carregar o que o usuário colou na própria pergunta.
 */
export default function LucasMarkdown({ content }) {
  if (!content) return null;

  return (
    <div className="text-[15px] leading-[1.7] text-[#25273a] dark:text-white/85">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // `last:mb-0` mantém o balão justo: a margem do último parágrafo
          // somaria ao padding e abriria um vão embaixo da resposta.
          p: ({ children }) => <p className="mb-2.5 last:mb-0">{children}</p>,
          strong: ({ children }) => (
            <strong className="font-semibold text-[#15161f] dark:text-white">
              {children}
            </strong>
          ),
          em: ({ children }) => <em className="italic">{children}</em>,
          code: ({ children }) => (
            <code className="rounded bg-black/[.055] px-1 py-0.5 font-mono text-[13px] text-[#15161f] dark:bg-white/[.10] dark:text-white">
              {children}
            </code>
          ),
          ul: ({ children }) => (
            <ul className="mb-2.5 list-disc space-y-1 pl-5 last:mb-0">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="mb-2.5 list-decimal space-y-1 pl-5 last:mb-0">{children}</ol>
          ),
          li: ({ children }) => <li className="pl-0.5">{children}</li>,
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noreferrer noopener"
              className="font-medium text-[#12a06c] underline underline-offset-2 dark:text-[#7ee2a8]"
            >
              {children}
            </a>
          ),
          // Bloco de código inteiro não pertence ao balão — existe o bloco
          // `snippet`, que tem cabeçalho e botão de copiar.
          pre: ({ children }) => (
            <pre className="mb-2.5 overflow-x-auto rounded-lg bg-black/[.045] p-2.5 font-mono text-[12.5px] last:mb-0 dark:bg-white/[.07]">
              {children}
            </pre>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
